import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@ngneat/transloco';
import { filter, map } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { registerLocaleData } from '@angular/common';
import localeKa from '@angular/common/locales/ka';
import localeEn from '@angular/common/locales/en';

export type Languages = 'ka' | 'en';


export enum LanguageEnum {
  Ge = 'ka',
  En = 'en',
}
export enum LanguageSymbolEnum {
  Ge = 'Geo',
  En = 'Eng',
}

@Injectable({ providedIn: 'root' })
export class TranslationService {
  readonly localStorage = inject(LocalStorageService);
  readonly translocoService = inject(TranslocoService);

  private activeLangCode = signal<string>(this.translocoService.getActiveLang());
  readonly activeLangCodeState = computed(() => this.activeLangCode())

  private languages = signal<Languages[]>(this.getAvailableLangs());
  readonly availableLangState = computed(this.languages);

  translationsLoaded = toSignal<boolean>(
    this.translocoService.events$.pipe(
      filter((event) => event.type === 'translationLoadSuccess'),
      map((event) => !!event)
    )
  );

  private getAvailableLangs(): Languages[] {
    return this.translocoService.getAvailableLangs().map((code) => (code) as Languages);
  }

  readonly langDisplaySymbol = computed(() => {

    const sources: Record<string, string> = {
      [LanguageEnum.Ge]: LanguageSymbolEnum.En,
      [LanguageEnum.En]: LanguageSymbolEnum.Ge,
    };
    return sources[this.activeLangCodeState()] || null;
  });

  transformSimbolToCode(lang: string) {
    const sources: Record<string, string> = {
      [LanguageSymbolEnum.Ge]: LanguageEnum.Ge,
      [LanguageSymbolEnum.En]: LanguageEnum.En,
    };
    return sources[lang] || null;
  }

  public setActiveLanguage(languageTypes: Languages) {
    this.translocoService.setActiveLang(languageTypes);
    this.localStorage.saveKey('smsLang', languageTypes);
    this.activeLangCode.update(() => languageTypes);
    if (languageTypes === 'ka') {
      registerLocaleData(localeKa, languageTypes);
    }
    if (languageTypes === 'en') {
      registerLocaleData(localeEn, languageTypes);
    }
  }
}
